import React, { useCallback, useEffect, useState } from 'react';
import { Row } from '@components/form/Form/Form';
import { isErrorResponse } from '@utils/utils';
import { useTranslation } from 'react-i18next';
import { serviceApi } from '@api/serviceApi';
import * as SelectBox from '@components/form/SelectBox/SelectBox';
import { Option } from '@components/form/SelectBox/SelectBox';
import './road-route-checker.scss';
import { RoadInfrastructureRouteEntity } from '@api/entities/replancity_RoadInfrastructureRoute';
import RoadSegmentCard from './RoadSegmentCard';
import { useToastContext } from '@context/toastContext';
import { usePresetsContext } from '@context/presetsContext';
import { RoadInfrastructureEntityName } from '@api/types/types';
import type * as schema from '@api/schema';
import { EntityServiceName } from '@api/enums/enums';
import SaveRouteAsSelectionSet from '@features/road-network-editor/RoadNetworkManager/save-route-as-selection-set/ui/SaveRouteAsSelectionSet';
import RouteCheckerBase from '@components/RouteCheckerBase/RouteCheckerBase';

const RouteChecker = ({
    entityServiceName,
    formFields,
}: {
    entityServiceName: EntityServiceName;
    formFields?: React.ReactNode;
}) => {
    const [modes, setModes] = useState<Option[]>([]);
    const { selectedPresetId: presetId } = usePresetsContext();
    const { t } = useTranslation();
    const { addToast } = useToastContext();

    useEffect(() => {
        if (presetId) {
            const abortController = new AbortController();
            void (async function () {
                const resp = await serviceApi.getModes<
                    schema.paths['/services/replancity_RoadInfrastructureService/listAllModes']['get']['parameters']['query']
                >(entityServiceName, { presetId }, abortController.signal);
                if (resp && Array.isArray(resp)) {
                    // if (!isErrorResponse(resp)) {
                    setModes(
                        resp.map(mode => ({
                            value: mode,
                        }))
                    );
                } else {
                    console.error("Replan. listAllModes response doesn't include array of modes");
                }
            })();
        }
    }, [presetId, entityServiceName]);

    const getRouteDetails = useCallback(
        async ({ entityServiceName, presetId, formDataObj, abortSignal }) => {
            const fromPoint = JSON.parse(formDataObj.fromPoint as string);
            const toPoint = JSON.parse(formDataObj.toPoint as string);

            const resp = await serviceApi.calculateAndGetRouteGeometry(
                entityServiceName,
                {
                    presetId,
                    fromLon: fromPoint.lng,
                    fromLat: fromPoint.lat,
                    toLon: toPoint.lng,
                    toLat: toPoint.lat,
                    mode: formDataObj.mode as string,
                },
                abortSignal
            );
            return resp;
        },
        []
    );

    const renderResult = useCallback(
        (details: RoadInfrastructureRouteEntity) =>
            details.segments.length ? (
                <>
                    <div className="road-route-checker__distances">
                        Distance: {details.distance}, straight distance: {details.straightDistance},
                        time: {details.time}
                    </div>
                    <div className="road-route-checker__controls">
                        <SaveRouteAsSelectionSet ids={details.segments.map(({ id }) => id)} />
                    </div>
                    <div className="road-route-checker__segments">
                        {details.segments.map(segment => (
                            <RoadSegmentCard key={segment.id} {...segment} />
                        ))}
                    </div>
                </>
            ) : null,
        []
    );

    const getFeatureStateIds = useCallback(
        details => details.segments.map(({ featureStateId }) => featureStateId),
        []
    );

    const errorHandler = useCallback(
        () => addToast(`${t('route-check.wrong-point-error-toast')}`, 'error'),
        []
    );

    return (
        <RouteCheckerBase<RoadInfrastructureRouteEntity>
            title={t('route-check.header-title')}
            submitBtnTitle={t('route-check.form-submit-btn')}
            layerEntityName={RoadInfrastructureEntityName.RoadSegment}
            entityServiceName={entityServiceName}
            showHeaderSpacer
            formFields={
                <>
                    <Row>
                        <SelectBox.Labeled
                            label={t('route-check.form-transport-mode')}
                            name="mode"
                            options={modes}
                        />
                    </Row>
                    {formFields}
                </>
            }
            getRouteDetails={getRouteDetails}
            getFeatureStateIds={getFeatureStateIds}
            onError={errorHandler}
            renderResult={renderResult}
        />
    );
};

export default RouteChecker;
